import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { IconMapPin, IconLoader2 } from '@tabler/icons-react'

/**
 * Niveles de granularidad para la búsqueda
 * - street_address: Dirección completa con número
 * - route: Calle sin número
 * - locality: Ciudad/Municipio
 * - administrative_area_level_2: Comarca/Provincia
 * - administrative_area_level_1: Comunidad Autónoma/Estado
 * - country: País
 */
export type AddressGranularity = 
  | 'street_address' 
  | 'route' 
  | 'locality' 
  | 'administrative_area_level_2'
  | 'administrative_area_level_1'
  | 'country'

/**
 * Datos estructurados de la dirección
 */
export interface AddressData {
  // Dirección formateada completa
  formatted_address: string
  
  // Componentes individuales
  street_number?: string      // Número de calle
  route?: string              // Nombre de la calle
  locality?: string           // Ciudad/Municipio
  sublocality?: string        // Barrio/Distrito
  administrative_area_level_2?: string  // Comarca/Provincia
  administrative_area_level_1?: string  // Comunidad Autónoma/Estado
  country?: string            // País (nombre completo)
  country_code?: string       // Código ISO del país (ES, FR, etc.)
  postal_code?: string        // Código postal
  
  // Coordenadas geográficas
  latitude?: number
  longitude?: number
  
  // Metadatos
  place_id?: string           // ID único de Google Places
  types?: string[]            // Tipos de lugar según Google
}

interface AddressAutocompleteProps {
  value?: string
  onChange: (address: AddressData) => void
  onInputChange?: (value: string) => void
  
  // Configuración de búsqueda
  granularity?: AddressGranularity
  countries?: string[]        // Códigos ISO de países (ej: ['es', 'fr', 'ad'])
  placeholder?: string
  
  // UI
  label?: string
  /** Text d'ajuda sota el camp (per defecte, en català). El formulari públic el passa traduït */
  hint?: string
  /** Text d'ajuda mentre carrega Google Maps (per defecte, en català) */
  loadingHint?: string
  /**
   * Idioma dels suggeriments de Google (noms de país i municipi). L'script es carrega un sol
   * cop per pàgina: compta el primer valor; si l'hoste canvia d'idioma després, no es recarrega
   */
  language?: string
  required?: boolean
  disabled?: boolean
  className?: string
  
  // API Key
  apiKey: string
}

export function AddressAutocomplete({
  value = '',
  onChange,
  onInputChange,
  granularity = 'locality', // Por defecto: nivel de ciudad
  countries = ['es', 'fr', 'ad'], // España, Francia, Andorra
  placeholder,
  label = 'Ubicació',
  hint = 'Comença a escriure i selecciona una opció de la llista',
  loadingHint = 'Carregant buscador...',
  language = 'ca', // Idioma per defecte: català
  required = false,
  disabled = false,
  className = '',
  apiKey,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement | null>(null) // Añadir inputRef
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState(value)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  // Sincronitzar inputValue quan value canvia externament (pre-omplert)
  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value)
    }
  }, [value])

  // Cargar script de Google Maps (solo una vez)
  useEffect(() => {
    if (!apiKey) {
      console.error('API Key de Google Maps no configurada')
      return
    }

    // Si Google Maps ya está cargado, simplemente marcar como listo
    if (window.google?.maps?.places) {
      setIsScriptLoaded(true)
      return
    }

    // Verificar si el script ya existe en el DOM
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`)
    if (existingScript) {
      // Esperar a que se cargue el script existente
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.places) {
          setIsScriptLoaded(true)
          clearInterval(checkLoaded)
        }
      }, 100)
      
      // Timeout después de 5 segundos
      setTimeout(() => clearInterval(checkLoaded), 5000)
      return
    }

    // Solo cargar el script si no existe
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${encodeURIComponent(language)}`
    script.async = true
    script.defer = true
    script.id = 'google-maps-script' // Añadir ID para identificar el script
    
    script.onload = () => {
      setIsScriptLoaded(true)
    }
    
    script.onerror = () => {
      console.error('Error al cargar Google Maps')
      setIsScriptLoaded(false)
    }
    
    document.head.appendChild(script)

    return () => {
      // No eliminar el script para evitar recarga
    }
  }, [apiKey]) // Solo dependencia de apiKey

  // `countries` i `onChange` arriben com a literals nous a cada render del formulari (`countries={[]}`).
  // Si l'efecte en depèn, cada tecla en qualsevol camp crea un altre `Autocomplete` sobre el mateix
  // input, i Google cobra cada instància com una sessió (SET-2026: 10.516 sessions per 304 adreces
  // triades). L'efecte depèn del contingut de la llista, i `onChange` es llegeix d'una ref.
  const countriesKey = countries.join(',')
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Inicializar el autocomplete cuando el script esté cargado
  useEffect(() => {
    if (!isScriptLoaded || !inputRef.current || !window.google?.maps?.places) {
      return
    }
    
    setIsLoading(false) // Marcar como cargado

    try {
      // Crear instancia de Autocomplete
      const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
        types: [granularity], // Configurar el nivel de granularidad
        componentRestrictions: { country: countriesKey ? countriesKey.split(',') : [] },
        fields: [
          'address_components',
          'formatted_address',
          'geometry',
          'place_id',
          'types',
        ],
      })

      // Listener para cuando se selecciona un lugar
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()

        if (!place.address_components) {
          console.warn('No se encontraron componentes de dirección')
          return
        }

        // Extraer y estructurar los datos
        const addressData: AddressData = {
          formatted_address: place.formatted_address || '',
          place_id: place.place_id,
          types: place.types,
        }

        // Procesar cada componente de la dirección
        place.address_components.forEach((component) => {
          const types = component.types

          if (types.includes('street_number')) {
            addressData.street_number = component.long_name
          }
          if (types.includes('route')) {
            addressData.route = component.long_name
          }
          if (types.includes('locality')) {
            addressData.locality = component.long_name
          }
          if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
            addressData.sublocality = component.long_name
          }
          if (types.includes('administrative_area_level_2')) {
            addressData.administrative_area_level_2 = component.long_name
          }
          if (types.includes('administrative_area_level_1')) {
            addressData.administrative_area_level_1 = component.long_name
          }
          if (types.includes('country')) {
            addressData.country = component.long_name
            addressData.country_code = component.short_name
          }
          if (types.includes('postal_code')) {
            addressData.postal_code = component.long_name
          }
        })

        // Añadir coordenadas si están disponibles
        if (place.geometry?.location) {
          addressData.latitude = place.geometry.location.lat()
          addressData.longitude = place.geometry.location.lng()
        }

        // Actualizar el input con el valor formateado
        setInputValue(addressData.formatted_address)
        
        // Notificar al padre
        onChangeRef.current(addressData)
      })

      autocompleteRef.current = autocomplete
    } catch (err) {
      console.error('Error al inicializar Google Places Autocomplete:', err)
      setError('Error al inicializar el buscador')
    }

    return () => {
      // Cleanup: remover listeners
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current)
        autocompleteRef.current = null
      }
    }
  }, [isScriptLoaded, granularity, countriesKey])

  // Manejar cambios en el input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    // Llamar callback opcional
    if (onInputChange) {
      onInputChange(newValue)
    }
  }

  // Obtener placeholder según granularidad
  const getPlaceholder = () => {
    if (placeholder) return placeholder

    const placeholders: Record<AddressGranularity, string> = {
      street_address: 'Carrer Major 123, Barcelona',
      route: 'Carrer Major, Barcelona',
      locality: 'Barcelona',
      administrative_area_level_2: 'Barcelonès',
      administrative_area_level_1: 'Catalunya',
      country: 'Espanya',
    }

    return placeholders[granularity] || 'Comença a escriure...'
  }

  if (error) {
    return (
      <div className={`space-y-2 ${className}`}>
        {label && (
          <Label className="text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </Label>
        )}
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label htmlFor="address-input" className="text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}

      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <IconLoader2 className="h-5 w-5 text-gray-400 animate-spin" />
          ) : (
            <IconMapPin className="h-5 w-5 text-gray-400" />
          )}
        </div>

        <Input
          ref={inputRef}
          id="address-input"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={getPlaceholder()}
          disabled={disabled || isLoading}
          required={required}
          className="pl-10"
        />
      </div>

      <p className="text-xs text-gray-500">
        {isLoading ? loadingHint : hint}
      </p>
    </div>
  )
}

// Hook personalizado para usar el componente fácilmente
export function useAddressAutocomplete() {
  const [address, setAddress] = useState<AddressData | null>(null)
  
  return {
    address,
    setAddress,
    // Helpers para acceder a datos específicos
    getCity: () => address?.locality || '',
    getProvince: () => address?.administrative_area_level_2 || '',
    getRegion: () => address?.administrative_area_level_1 || '',
    getCountry: () => address?.country || '',
    getCountryCode: () => address?.country_code || '',
    getCoordinates: () => ({
      lat: address?.latitude,
      lng: address?.longitude,
    }),
  }
}
